/**
 * Safer structured clone with graceful fallback.
 * - Uses structuredClone when available
 * - Falls back to JSON for plain data
 * - Supports Date/RegExp/Map/Set/TypedArrays via a tiny custom copier
 */

function isObjectLike(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function cloneWithFallback<T>(value: T, seen = new WeakMap<object, any>()): T {
  // Prefer native structuredClone when present
  // (present in modern browsers / Node 17+ / Deno)
  // @ts-ignore
  if (typeof globalThis.structuredClone === 'function') {
    // @ts-ignore
    return globalThis.structuredClone(value);
  }

  // Primitive fast-path
  if (!isObjectLike(value)) return value;

  if (seen.has(value as object)) return seen.get(value as object);

  // Date
  if (value instanceof Date) return new Date(value.getTime()) as unknown as T;
  // RegExp
  if (value instanceof RegExp) return new RegExp(value.source, value.flags) as unknown as T;
  // Map
  if (value instanceof Map) {
    const out = new Map();
    seen.set(value, out);
    for (const [k, v] of value) out.set(cloneWithFallback(k, seen), cloneWithFallback(v, seen));
    return out as unknown as T;
  }
  // Set
  if (value instanceof Set) {
    const out = new Set();
    seen.set(value, out);
    for (const v of value) out.add(cloneWithFallback(v, seen));
    return out as unknown as T;
  }
  // ArrayBuffer / Typed arrays
  if (ArrayBuffer.isView(value)) {
    // @ts-ignore
    return new (value.constructor as any)(value as any) as unknown as T;
  }
  if (value instanceof ArrayBuffer) {
    return value.slice(0) as unknown as T;
  }

  // Array
  if (Array.isArray(value)) {
    const out: any[] = new Array(value.length);
    seen.set(value, out);
    for (let i = 0; i < value.length; i++) {
      out[i] = cloneWithFallback(value[i], seen);
    }
    return out as unknown as T;
  }

  // Plain object (ignore prototype chain)
  const out: Record<string, unknown> = {};
  seen.set(value, out);
  for (const k of Object.keys(value)) {
    // @ts-ignore
    out[k] = cloneWithFallback((value as any)[k], seen);
  }
  return out as unknown as T;
}

/**
 * Public clone helpers
 */
export function clone<ItemType>(item: ItemType): ItemType {
  // @ts-ignore
  return typeof globalThis.structuredClone === 'function'
    ? // @ts-ignore
      globalThis.structuredClone(item)
    : cloneWithFallback(item);
}

export function cloneOrNull<ItemType = object>(item: ItemType | undefined | null): ItemType | null {
  if (item == null) return null;
  return clone(item);
}

export function cloneOrUndefined<ItemType = object>(item: ItemType | undefined | null): ItemType | undefined {
  if (item == null) return undefined;
  return clone(item);
}

/** ------------------------------------------------------------------------------------
 * Deep equality (cycle-safe), covering common JS types and avoiding falsy confusion.
 * ---------------------------------------------------------------------------------- */

function isNaNNumber(x: unknown): boolean {
  return typeof x === 'number' && Number.isNaN(x);
}

function sameZero(a: number, b: number) {
  // Distinguish +0 and -0 like Object.is
  return a === b && 1 / a === 1 / b;
}

function keysOf(o: object): string[] {
  return Object.keys(o);
}

export function compare(a: any, b: any, _seen = new WeakMap<object, object>()): boolean {
  if (a === b) {
    // covers primitives (except NaN) and identical references
    // + handle +0/-0
    if (typeof a === 'number' && typeof b === 'number') return sameZero(a, b);
    return true;
  }

  // NaN === NaN (deep equal semantics)
  if (isNaNNumber(a) && isNaNNumber(b)) return true;

  // types differ
  if (typeof a !== typeof b) return false;

  // null vs object
  if (!isObjectLike(a) || !isObjectLike(b)) return false;

  // Cycle handling
  const cached = _seen.get(a);
  if (cached === b) return true;
  _seen.set(a, b);

  // Date
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  }

  // RegExp
  if (a instanceof RegExp || b instanceof RegExp) {
    return a instanceof RegExp && b instanceof RegExp && a.source === b.source && a.flags === b.flags;
  }

  // Map
  if (a instanceof Map || b instanceof Map) {
    if (!(a instanceof Map && b instanceof Map)) return false;
    if (a.size !== b.size) return false;
    for (const [k, v] of a) {
      if (!b.has(k)) return false;
      if (!compare(v, b.get(k), _seen)) return false;
    }
    return true;
  }

  // Set
  if (a instanceof Set || b instanceof Set) {
    if (!(a instanceof Set && b instanceof Set)) return false;
    if (a.size !== b.size) return false;
    // For Sets, we need "structural" presence: every element in a must be in b
    // A simple but effective approach: for each element in a, look for a deep-equal match in b
    const used = new Set<any>();
    outer: for (const av of a) {
      let matched = false;
      let idx = 0;
      for (const bv of b) {
        if (!used.has(idx) && compare(av, bv, _seen)) {
          used.add(idx);
          matched = true;
          break;
        }
        idx++;
      }
      if (!matched) return false;
    }
    return true;
  }

  // Typed arrays
  if (ArrayBuffer.isView(a) || ArrayBuffer.isView(b)) {
    if (!(ArrayBuffer.isView(a) && ArrayBuffer.isView(b))) return false;
    if ((a as any).constructor !== (b as any).constructor) return false;
    const ta = a as unknown as { length: number; [i: number]: number };
    const tb = b as unknown as { length: number; [i: number]: number };
    if (ta.length !== tb.length) return false;
    for (let i = 0; i < ta.length; i++) if (!sameZero(ta[i], tb[i])) return false;
    return true;
  }

  // Array
  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray || bIsArray) {
    if (!(aIsArray && bIsArray)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!compare(a[i], b[i], _seen)) return false;
    }
    return true;
  }

  // Plain objects (ignore prototype)
  const ak = keysOf(a);
  const bk = keysOf(b);
  if (ak.length !== bk.length) return false;

  // Key set equality
  for (const k of ak) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
  }
  // Value equality
  for (const k of ak) {
    if (!compare(a[k], b[k], _seen)) return false;
  }
  return true;
}

/** ------------------------------------------------------------------------------------
 * Deep diff (cycle-safe). Produces { result: boolean, reason: DiffTree } at top-level.
 * For non-top-level calls, returns a DiffTree object.
 * ---------------------------------------------------------------------------------- */

type DiffLeaf = { before: any; after: any };
type DiffTree = { [key: string]: DiffLeaf | DiffTree } | DiffLeaf | { [index: number]: DiffTree | DiffLeaf };

export function compareAnalyze(
  obj1: any,
  obj2: any,
  isTopLevel: boolean = true,
  _seen = new WeakMap<object, object>()
): { result: boolean; reason: any } | any {
  // Fast path: strictly equal or deep-equal early out
  if (compare(obj1, obj2)) {
    return isTopLevel ? { result: true, reason: {} } : {};
  }

  // Handle null/undefined asymmetry
  if (obj1 == null || obj2 == null) {
    const leaf: DiffLeaf = { before: obj1, after: obj2 };
    return isTopLevel ? { result: false, reason: leaf } : leaf;
  }

  // Cycle handling
  if (isObjectLike(obj1) && isObjectLike(obj2)) {
    const seen = _seen.get(obj1);
    if (seen === obj2) return isTopLevel ? { result: true, reason: {} } : {};
    _seen.set(obj1, obj2);
  }

  // Array handling (positional diff)
  if (Array.isArray(obj1) || Array.isArray(obj2)) {
    if (!(Array.isArray(obj1) && Array.isArray(obj2))) {
      const leaf: DiffLeaf = { before: obj1, after: obj2 };
      return isTopLevel ? { result: false, reason: leaf } : leaf;
    }

    const maxLen = Math.max(obj1.length, obj2.length);
    const diffs: Record<number, DiffTree | DiffLeaf> = {};
    let has = false;

    for (let i = 0; i < maxLen; i++) {
      const a = obj1[i];
      const b = obj2[i];
      if (compare(a, b)) continue;
      const sub = compareAnalyze(a, b, false, _seen);
      diffs[i] = sub;
      has = true;
    }

    if (!has) return isTopLevel ? { result: true, reason: {} } : {};
    return isTopLevel ? { result: false, reason: diffs } : diffs;
  }

  // Objects (compare keys + values)
  if (isObjectLike(obj1) && isObjectLike(obj2)) {
    const aKeys = new Set(Object.keys(obj1));
    const bKeys = new Set(Object.keys(obj2));
    const all = new Set([...aKeys, ...bKeys]);

    const out: Record<string, DiffTree | DiffLeaf> = {};
    let has = false;

    for (const k of all) {
      const aHas = aKeys.has(k);
      const bHas = bKeys.has(k);
      const aVal = (obj1 as any)[k];
      const bVal = (obj2 as any)[k];

      if (!aHas || !bHas) {
        out[k] = { before: aHas ? aVal : undefined, after: bHas ? bVal : undefined };
        has = true;
        continue;
      }

      if (!compare(aVal, bVal)) {
        const sub = compareAnalyze(aVal, bVal, false, _seen);
        out[k] = sub;
        has = true;
      }
    }

    if (!has) return isTopLevel ? { result: true, reason: {} } : {};
    return isTopLevel ? { result: false, reason: out } : out;
  }

  // Fallback leaf (mismatched primitives / types)
  const leaf: DiffLeaf = { before: obj1, after: obj2 };
  return isTopLevel ? { result: false, reason: leaf } : leaf;
}

/**
 * Converts compareAnalyze output to a readable Markdown string
 * @param analysis The output from compareAnalyze
 * @param indentLevel Current indentation level
 * @returns Formatted Markdown string
 */
export function diffToMarkdown(analysis: any, indentLevel = 0): string {
  // If no changes or empty
  if (!analysis || Object.keys(analysis).length === 0) {
    return '*No differences*';
  }

  // If this is the top-level result from compareAnalyze
  if ('result' in analysis && 'reason' in analysis) {
    if (analysis.result === true) {
      return '*Objects are identical*';
    }
    return diffToMarkdown(analysis.reason, indentLevel);
  }

  const indent = '  '.repeat(indentLevel);
  let markdown = '';

  // Handle leaf nodes (direct value comparisons)
  if ('before' in analysis && 'after' in analysis) {
    const formatValue = (val: any): string => {
      if (val === undefined) return '`undefined`';
      if (val === null) return '`null`';
      if (typeof val === 'string') return `"${val}"`;
      if (typeof val === 'object') return `\`${JSON.stringify(val)}\``;
      return `\`${val}\``;
    };

    const before = formatValue(analysis.before);
    const after = formatValue(analysis.after);

    return `${indent}Changed from ${before} to ${after}`;
  }

  // Process object or array
  Object.keys(analysis).forEach((key, index, array) => {
    const value = analysis[key];
    const isArrayIndex = !isNaN(Number(key));

    if (isArrayIndex) {
      markdown += `${indent}- Index **${key}**:\n`;
    } else {
      markdown += `${indent}- Property **${key}**:\n`;
    }

    markdown += `${diffToMarkdown(value, indentLevel + 1)}\n`;

    // Add a separator between top-level entries for better readability
    if (indentLevel === 0 && index < array.length - 1) {
      markdown += `${indent}---\n`;
    }
  });

  return markdown;
}

/** ------------------------------------------------------------------------------------
 * Backup<T>: snapshot + compare + commit
 * - For object types, commit mutates original's fields to match item
 * - For primitives, commit returns the new value (since we can't mutate primitives by ref)
 * ---------------------------------------------------------------------------------- */

export class Backup<TItem> {
  original: TItem | null;
  item: TItem | null;

  constructor(item: TItem | null = null) {
    this.original = item;
    this.item = clone(item);
  }

  backup(item: TItem | null = null): void {
    this.original = item;
    this.item = clone(item);
  }

  /** Return the working copy (cloned) */
  value(): TItem | null {
    return this.item;
  }

  /** Reset working copy back to original */
  reset(): void {
    this.item = clone(this.original);
  }

  /**
   * Commit changes back into the original.
   * - For objects, shallow-assign fields from working copy.
   * - For primitives, returns the new committed value (caller must reassign).
   */
  commit(): TItem | undefined {
    if (!this.changed()) return undefined;
    if (this.original == null || this.item == null) return undefined;

    if (isObjectLike(this.original) && isObjectLike(this.item)) {
      // Shallow-assign to preserve original reference
      Object.keys(this.original).forEach(k => {
        // @ts-ignore
        delete this.original![k];
      });
      Object.assign(this.original as any, this.item as any);
      return undefined;
    }

    // Primitive path: cannot mutate by reference—return new value to caller
    // so they can reassign their variable/property if needed.
    // Example: const committed = backup.commit(); if (committed !== undefined) foo = committed;
    // @ts-ignore
    return this.item as TItem;
  }

  changed(): boolean {
    return !compare(this.original, this.item);
  }

  /**
   * Analyze changes between the original and current item.
   * result=false => has differences
   */
  changedAnalysis(): { result: boolean; reason: any } {
    return compareAnalyze(this.original, this.item);
  }
}
